import { apiService } from "api/apiService";
import type {
  ClassroomMentorClassesResource,
  EducationLevelResource,
  MentorClassResource,
  PortfolioShallowStudentResource,
  ScheduleWithEducationLevelsResource,
} from "api/generated";
import {
  selectionEducationLevelFromDto,
  selectionLessonGroupFromDto,
  selectionMentorClassFromDto,
  selectionStudentFromDto,
} from "shared/mappers";
import type {
  MyLessonClass,
  SelectionEducationLevel,
  SelectionLessonGroup,
  SelectionMentorClass,
  SelectionStudent,
} from "shared/types";

// Only these four fields are read, so the shape is declared here.
type MyLessonClassesResponse = {
  lesson_classes: Record<
    string,
    {
      id: number;
      name: string;
      subject_shortage: string;
      subject_name: string | null;
    }
  >;
};

export const fetchMentorClasses = async (): Promise<SelectionMentorClass[]> => {
  const {
    data: { data: mentorClasses },
  } = await apiService.get<{ data: MentorClassResource[] }>(
    "api/student-selection/mentor-classes",
  );

  return mentorClasses.map(selectionMentorClassFromDto);
};

/**
 * An optional lesson group (schedule id) or mentor class (class code) narrows
 * the list server-side to that group's or class's students; without filters
 * every student the user may select is fetched.
 */
export const fetchStudentsList = async ({
  lessonGroup = null,
  mentorClass = null,
}: {
  lessonGroup?: number | null;
  mentorClass?: string | null;
} = {}): Promise<SelectionStudent[]> => {
  const params: Record<string, number | string> = {};

  if (lessonGroup !== null) {
    params.lesson_group = lessonGroup;
  }

  if (mentorClass !== null) {
    params.mentor_class = mentorClass;
  }

  const {
    data: { data: students },
  } = await apiService.get<{ data: PortfolioShallowStudentResource[] }>(
    "api/student-selection/students",
    { params },
  );

  return students.map(selectionStudentFromDto);
};

export const fetchLessonGroups = async (): Promise<SelectionLessonGroup[]> => {
  const {
    data: { data: lessonGroups },
  } = await apiService.get<{ data: ScheduleWithEducationLevelsResource[] }>(
    "api/student-selection/lesson-groups",
  );

  return lessonGroups.map(selectionLessonGroupFromDto);
};

export const fetchEducationLevels = async (): Promise<
  SelectionEducationLevel[]
> => {
  const {
    data: { data: educationLevels },
  } = await apiService.get<{ data: EducationLevelResource[] }>(
    "api/student-selection/education-levels",
  );

  return educationLevels.map(selectionEducationLevelFromDto);
};

/**
 * The groups the user actually *teaches*, whatever their role —
 * `fetchLessonGroups` falls back to every school group for non-teacher roles.
 */
export const fetchMyLessonClasses = async (): Promise<MyLessonClass[]> => {
  const {
    data: { data },
  } = await apiService.get<{ data: MyLessonClassesResponse }>(
    "api/my-lesson-classes",
  );

  return Object.values(data.lesson_classes).map((lessonClass) => ({
    id: lessonClass.id,
    name: lessonClass.name,
    subject: lessonClass.subject_shortage,
    subjectName: lessonClass.subject_name,
  }));
};

/**
 * The classes the current user actually *mentors* — narrower than
 * `fetchMentorClasses`, which falls back to every school class for
 * non-teacher roles.
 */
export const fetchMyMentorClasses = async (): Promise<string[]> => {
  const {
    data: { data },
  } = await apiService.get<{ data: ClassroomMentorClassesResource }>(
    "api/my-mentor-classes",
  );

  return data.mentor_classes;
};
